import React, { useEffect, useMemo, useState } from 'react';
import { useSettings } from '../../../Settings/SettingsProvider';
import { cn } from '@ais-ui/utils';
import { useMapLegendSettings } from './useMapLegendSettings';
import { useLayers } from '@ais-ui/react-openlayers';

interface LegendItemProps {
  color?: string | null;
  icon?: React.ElementType | null;
  category: string;
  removable?: boolean;
  onRemove?: (event: React.MouseEvent, category: string) => void;
  id?: string;
  iconColor?: string | null;
}

export function LegendItem({
  color,
  icon: Icon,
  category,
  removable = false,
  onRemove,
  id,
  iconColor,
}: LegendItemProps) {
  const { layers, toggleLayerVisibility } = useLayers();
  const { settings } = useSettings();

  const notShownLegendItems = useMemo(() => {
    return settings['ribbonPlugin.settings']?.['ship_color_noshow_sel'] || [];
  }, [settings]);

  const [isActive, setIsActive] = useState<boolean>(
    !notShownLegendItems.includes(category),
  );

  useEffect(() => {
    setIsActive(!notShownLegendItems.includes(category));
  }, [category, notShownLegendItems]);

  const { onActivate } = useMapLegendSettings();

  const extendedOnActivate = (category: string) => {
    if (!isActive) {
      const layer = layers.find((l) => l.name.toLowerCase() === 'vessels');

      if (layer) {
        toggleLayerVisibility(layer.layer, true);
      }
    }

    setIsActive(!isActive);
    onActivate(isActive, category, id);
  };

  return (
    <div
      onClick={() => extendedOnActivate(category)}
      className={cn(
        'w-32 min-w-fit min-h-fit bg-card flex items-center relative rounded-md shadow-sm hover:bg-accent hover:cursor-pointer py-1',
        !isActive && 'opacity-50',
      )}
    >
      <div>
        {isActive && color && (
          <div
            className="absolute -top-1 -left-1 h-3 w-3 z-10 shadow-sm rounded-lg"
            style={{ backgroundColor: color }}
          ></div>
        )}

        {isActive && Icon && (
          <div className="absolute -top-1 -left-1 h-3 w-3 z-10 shadow-sm rounded-lg">
            <Icon style={{ color: iconColor }} />
          </div>
        )}
        <div className="ml-4 mr-1 min-w-fit min-h-fit z-0 whitespace-normal text-xs">
          {category}
        </div>
      </div>
      {removable && onRemove && (
        <div
          onClick={(event) => onRemove(event, category)}
          className="absolute mb-0.5 right-1.5 text-muted-foreground hover:text-foreground cursor-pointer"
        >
          &times;
        </div>
      )}
    </div>
  );
}
